import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../utils/apiConfig';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Send, CheckCircle, XCircle, Search, RefreshCw, Phone, User, Filter, Clock, FileText } from 'lucide-react';

export default function TemplateLogsPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [triggerFilter, setTriggerFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [templateFilter, setTemplateFilter] = useState('all');
  const [dateFilter, setDateFilter] = useState('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [phoneSearch, setPhoneSearch] = useState('');
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const LIMIT = 50;

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { setPage(0); }, [triggerFilter, statusFilter, templateFilter, dateFilter, customFrom, customTo, phoneSearch]);
  useEffect(() => { fetchLogs(); }, [page, triggerFilter, statusFilter, templateFilter, dateFilter, customFrom, customTo, phoneSearch]);

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/wa-template-logs/stats`);
      setStats(data);
    } catch (err) { console.error('Stats error:', err); }
  };

  const dateRange = (key) => {
    const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const now = new Date();
    const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    switch (key) {
      case 'today': return { start_date: fmt(t), end_date: fmt(t) };
      case 'yesterday': { const y = new Date(t); y.setDate(y.getDate() - 1); return { start_date: fmt(y), end_date: fmt(y) }; }
      case 'this_week': { const s = new Date(t); s.setDate(s.getDate() - ((s.getDay() + 6) % 7)); return { start_date: fmt(s), end_date: fmt(t) }; }
      case 'mtd': return { start_date: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end_date: fmt(t) };
      case 'this_month': return { start_date: fmt(new Date(now.getFullYear(), now.getMonth(), 1)), end_date: fmt(new Date(now.getFullYear(), now.getMonth() + 1, 0)) };
      case 'custom': return { start_date: customFrom || '', end_date: customTo || '' };
      default: return { start_date: '', end_date: '' };
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: LIMIT, skip: page * LIMIT };
      if (triggerFilter !== 'all') params.trigger = triggerFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (templateFilter !== 'all') params.template = templateFilter;
      const { start_date, end_date } = dateRange(dateFilter);
      if (start_date) params.start_date = start_date;
      if (end_date) params.end_date = end_date;
      if (phoneSearch.trim()) params.phone = phoneSearch.trim();
      const { data } = await axios.get(`${API}/wa-template-logs`, { params });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (err) { console.error('Logs error:', err); }
    finally { setLoading(false); }
  };

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
  };

  const triggerLabel = (t) => {
    const map = { booking_confirmation: 'Booking Confirm', booking_reminder: 'Reminder', review_request: 'Review', broadcast: 'Broadcast' };
    return map[t] || t;
  };

  const triggerColor = (t) => {
    const map = {
      booking_confirmation: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
      booking_reminder: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
      review_request: 'bg-blue-500/15 text-blue-400 border-blue-500/30',
      broadcast: 'bg-purple-500/15 text-purple-400 border-purple-500/30',
    };
    return map[t] || 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30';
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="template-logs-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Template Message Logs</h1>
            <p className="text-zinc-400 text-sm mt-1">Track all WhatsApp template messages sent via triggers</p>
          </div>
          <Button onClick={() => { fetchLogs(); fetchStats(); }} variant="outline" size="sm" data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="stats-cards">
            <Card className="bg-zinc-900 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-zinc-800"><Send className="w-4 h-4 text-zinc-300" /></div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Total Sent</p>
                  <p className="text-xl font-bold text-white">{stats.total}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-500/10"><CheckCircle className="w-4 h-4 text-emerald-400" /></div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Delivered</p>
                  <p className="text-xl font-bold text-emerald-400">{stats.success_count}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-500/10"><XCircle className="w-4 h-4 text-red-400" /></div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Failed</p>
                  <p className="text-xl font-bold text-red-400">{stats.failed_count}</p>
                </div>
              </div>
            </Card>
            <Card className="bg-zinc-900 border-zinc-800 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-zinc-800"><FileText className="w-4 h-4 text-zinc-300" /></div>
                <div>
                  <p className="text-xs text-zinc-500 uppercase tracking-wider">Templates</p>
                  <p className="text-xl font-bold text-white">{stats.by_template ? Object.keys(stats.by_template).length : 0}</p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Trigger breakdown */}
        {stats?.by_trigger && Object.keys(stats.by_trigger).length > 0 && (
          <div className="flex gap-3 flex-wrap">
            {Object.entries(stats.by_trigger).map(([trigger, count]) => (
              <span key={trigger} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border ${triggerColor(trigger)}`}>
                {triggerLabel(trigger)}: {count}
              </span>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center" data-testid="filters">
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              placeholder="Search by phone..."
              value={phoneSearch}
              onChange={e => setPhoneSearch(e.target.value)}
              className="pl-10 bg-zinc-900 border-zinc-700 text-white"
              data-testid="phone-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-zinc-500" />
            <select
              value={triggerFilter}
              onChange={e => setTriggerFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300"
              data-testid="trigger-filter"
            >
              <option value="all">All Triggers</option>
              <option value="booking_confirmation">Booking Confirm</option>
              <option value="booking_reminder">Reminder</option>
              <option value="review_request">Review</option>
              <option value="broadcast">Broadcast</option>
            </select>
            <select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300"
              data-testid="status-filter"
            >
              <option value="all">All Status</option>
              <option value="success">Success</option>
              <option value="failed">Failed</option>
            </select>
            <select
              value={templateFilter}
              onChange={e => setTemplateFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300"
              data-testid="template-filter"
            >
              <option value="all">All Templates</option>
              {stats?.by_template && Object.keys(stats.by_template).map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
            <select
              value={dateFilter}
              onChange={e => setDateFilter(e.target.value)}
              className="bg-zinc-900 border border-zinc-700 rounded-md px-3 py-2 text-sm text-zinc-300"
              data-testid="date-filter"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="yesterday">Yesterday</option>
              <option value="this_week">This Week</option>
              <option value="mtd">Month to Date</option>
              <option value="this_month">This Month</option>
              <option value="custom">Custom Range</option>
            </select>
            {dateFilter === 'custom' && (
              <>
                <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white w-[150px]" data-testid="date-from" />
                <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)}
                  className="bg-zinc-900 border-zinc-700 text-white w-[150px]" data-testid="date-to" />
              </>
            )}
          </div>
        </div>

        {/* Logs Table */}
        <Card className="bg-zinc-900 border-zinc-800 overflow-hidden" data-testid="logs-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-800 text-left">
                  <th className="px-4 py-3 text-zinc-400 font-medium">Timestamp</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Template</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Trigger</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Phone / Name</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Parameters</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Message ID</th>
                  <th className="px-4 py-3 text-zinc-400 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">Loading...</td></tr>
                ) : logs.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-zinc-500">No template messages logged yet</td></tr>
                ) : logs.map((log, i) => (
                  <tr key={log.id || i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors" data-testid={`log-row-${i}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5 text-zinc-400">
                        <Clock className="w-3.5 h-3.5 shrink-0" />
                        <span className="whitespace-nowrap text-xs">{formatTime(log.timestamp)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-white font-medium text-xs bg-zinc-800 px-2 py-1 rounded">{log.template_name || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${triggerColor(log.trigger)}`}>
                        {triggerLabel(log.trigger)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <Phone className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
                        <span className="text-zinc-300 text-xs">{log.phone}</span>
                      </div>
                      {log.player_name && (
                        <div className="flex items-center gap-1.5 mt-0.5">
                          <User className="w-3.5 h-3.5 text-zinc-600 shrink-0" />
                          <span className="text-zinc-500 text-xs">{log.player_name}</span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-400 text-xs max-w-[200px] truncate block" title={log.parameters}>{log.parameters || '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-zinc-500 text-xs font-mono">{log.message_id ? log.message_id.substring(0, 16) + (log.message_id.length > 16 ? '...' : '') : '-'}</span>
                    </td>
                    <td className="px-4 py-3">
                      {log.status === 'success' ? (
                        <div className="flex flex-col gap-0.5" title={log.delivery_error || ''}>
                          <span className="inline-flex items-center gap-1 text-emerald-400 text-xs">
                            <CheckCircle className="w-3.5 h-3.5" /> Sent
                          </span>
                          {log.delivery_status && (
                            <span className={`inline-flex items-center gap-1 text-xs ${
                              log.delivery_status === 'delivered' ? 'text-emerald-300'
                              : log.delivery_status === 'read' ? 'text-emerald-200'
                              : log.delivery_status === 'failed' ? 'text-red-400'
                              : 'text-amber-300'
                            }`}>
                              → {log.delivery_status}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-red-400 text-xs" title={log.error}>
                          <XCircle className="w-3.5 h-3.5" /> Failed
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-zinc-800">
              <span className="text-xs text-zinc-500">Showing {page * LIMIT + 1}-{Math.min((page + 1) * LIMIT, total)} of {total}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">Prev</Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">Next</Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
